import PhoneInput from 'react-phone-input-2'
import useDetectCountry from '@hooks/useDetectCountry'
import { useIdentifierSwap } from '@hooks/useIdentifierSwap'
import { IdentifierMode } from '@lib/utils/identifier'
import React, { useEffect, useState } from 'react'
import { LinguiJSMessageDescriptor } from '@lib/types/common'
import MsgTrans from '@components/common/i18n/message-trans'
import Tooltip from '@components/common/tooltip'
import { Info } from 'react-feather'
import { t } from '@lingui/macro'

type Props = {
  id: string
  name: string
  label?: string | LinguiJSMessageDescriptor
  helpText?: string
  errorText?: string
  errorList?: Array<string>
  autoFocus?: boolean
  placeholder?: string
  required?: boolean
  disabled?: boolean
  value: any
  mode?: IdentifierMode
  // Opt-in. Without it this stays a phone-only widget and never intercepts
  // keystrokes, which is what every consumer except the register form wants.
  allowEmail?: boolean
  // Shown instead of `label` once the field has decided what it is.
  phoneLabel?: string | LinguiJSMessageDescriptor
  emailLabel?: string | LinguiJSMessageDescriptor
  // Short badge beside the label. Used where this field is the one piece of
  // information we are missing and every other field on the form is prefilled.
  labelBadge?: string
  // The WhatsApp tooltip is signup copy - it tells the reader they can
  // "register" with another number. Opt out where the form is not a signup.
  hideWhatsAppTooltip?: boolean
  onChange: (value: string, mode: IdentifierMode) => void
  handleEnterKeyPress?: () => void
  onKeyUp?: (e: React.FormEvent<HTMLInputElement>) => void
  countryCode?: string
  isRightToLeftPage?: boolean
} & typeof defaultProps

const defaultProps = {
  label: '',
  helpText: '',
  errorText: '',
  errorList: [],
  placeholder: '',
  required: false,
  disabled: false,
}

const CustomPhoneInput = (props: Props): JSX.Element => {
  const {
    value,
    mode = 'phone',
    allowEmail = false,
    phoneLabel,
    emailLabel,
    labelBadge,
    hideWhatsAppTooltip = false,
    onChange,
    label,
    id,
    name,
    required,
    autoFocus,
    placeholder,
    helpText,
    errorText,
    errorList,
    handleEnterKeyPress,
    disabled,
    countryCode: countryCodeFromProps,
    isRightToLeftPage,
  } = props

  const { countryCode: countryCodeFromHook } = useDetectCountry({
    isLowerCase: true,
  })

  // If the phone field is to be populated then skip setting country code
  // Because populated data will already contain the country code
  const getCountryCode = (value: string) =>
    value ? undefined : countryCodeFromProps || countryCodeFromHook

  const [countryCode, setCountryCode] = useState<string>(getCountryCode(value))
  const {
    showPhoneWidget,
    identifierState,
    plainInputRef,
    swapHandlers,
    handlePlainChange,
    onPhoneWidgetFocus,
  } = useIdentifierSwap(mode, value, onChange, handleEnterKeyPress)
  // Opted-out forms stay on the phone widget unconditionally, exactly as
  // before; only the combined field gets the neutral resting state.
  const renderPhoneWidget = !allowEmail || showPhoneWidget

  // There is a delay between rendering and resolving of country code from hook
  // Hence we will update the country code once it is resovled from hook
  useEffect(() => {
    setCountryCode(getCountryCode(value))
  }, [countryCodeFromHook])

  // Narrows once the field knows what it is. The caller passes all three so
  // the strings stay next to the rest of its copy, and lingui can find them.
  const resolvedLabel =
    (identifierState === 'phone' && phoneLabel) ||
    (identifierState === 'email' && emailLabel) ||
    label

  return (
    <div className="flex flex-col space-y-1">
      {label && (
        <label
          htmlFor={id}
          className="text-grey text-md font-600 flex items-center"
        >
          <MsgTrans msg={resolvedLabel} />
          {labelBadge && (
            // Inline rather than palette classes: neither colour exists in
            // tailwind.config.js, and 1.7.5 has no arbitrary-value syntax to
            // reach them. 5.69:1, so comfortably AA at this size.
            <span
              className="ml-2 px-2 py-1 text-xs font-500 rounded"
              style={{ backgroundColor: '#E7EDF4', color: '#1D4ED8' }}
            >
              {labelBadge}
            </span>
          )}
          {renderPhoneWidget && !hideWhatsAppTooltip && (
            <Tooltip
              className="ml-2"
              content={t`If you do not have a WhatsApp enabled phone number, then you can register with any other personal phone number.`}
              showOnHover
            >
              {/* grey-700 is #999 - 2.85:1 on white, under the 3:1 that 1.4.11
                  asks of a non-text control. */}
              <Info className="cursor-pointer text-grey-800" size={16} />
            </Tooltip>
          )}
        </label>
      )}
      {!renderPhoneWidget ? (
        <input
          ref={plainInputRef}
          id={id}
          name={name}
          type="text"
          inputMode="email"
          autoComplete="email"
          className="border border-grey-500 w-full rounded py-3 px-4 text-grey leading-tight focus:outline-none"
          value={value}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          onChange={handlePlainChange}
          onKeyDown={(event) =>
            event.key === 'Enter' && handleEnterKeyPress?.()
          }
        />
      ) : (
        <PhoneInput
          containerClass="font-sans text-base"
          inputClass={`border border-grey-500 w-full rounded py-3 text-grey leading-tight focus:outline-none ${
            isRightToLeftPage ? 'rtl-phone-input pl-4 pr-12' : 'pl-16 pr-4'
          }`}
          searchStyle={{
            marginBottom: '2px',
          }}
          // The dropdown is absolutely positioned inside whatever scroll
          // container it lands in - in the trial-details modal that is an
          // `overflow-auto` body, which clips the list and grows a second
          // scrollbar. The library cannot portal it out, so keep it short
          // enough to fit; the search box covers finding anything further
          // down.
          dropdownStyle={{ maxHeight: '150px' }}
          buttonClass={isRightToLeftPage && 'rtl-input'}
          enableSearch
          country={countryCode}
          value={value}
          // Only the combined field needs an editable dial code, and only it
          // gets one. With `false` the library's handleInput returns early on
          // any value not starting with the dial code, so selecting the number
          // and deleting it is swallowed and the field cannot be cleared - and
          // a field that cannot be cleared cannot be turned into an email one.
          // Every phone-only caller keeps the dial code fixed, as it was
          // before this field learned to take an email.
          countryCodeEditable={allowEmail}
          // Enter submits on the phone-only forms that ask it to. The swap
          // handlers below carry their own Enter case, but they are only
          // spread in when `allowEmail` is set, so without this the key does
          // nothing on every other consumer.
          onEnterKeyPress={handleEnterKeyPress}
          onChange={(
            nextValue: string,
            data: { dialCode?: string } | Record<string, never>
          ) => {
            // Only the combined field needs this, and only it gets it - every
            // other caller of this component keeps the old behaviour.
            //
            // Deleting one digit at a time walks the value below the dial
            // code, at which point the library cannot place it in any country
            // and hands back an empty `data`. "+91 9" becomes "9", which it
            // renders as a blank box while the flag stays put and no further
            // edit fires a change, so the field can never go back to being a
            // plain input you could type an email into.
            if (!allowEmail) return onChange(nextValue, 'phone')

            const dialDigits = (data?.dialCode ?? '').replace(/\D/g, '')
            const valueDigits = nextValue.replace(/\D/g, '')
            const hasNumber =
              Boolean(dialDigits) && valueDigits.length > dialDigits.length
            onChange(hasNumber ? nextValue : '', 'phone')
          }}
          onFocus={allowEmail ? onPhoneWidgetFocus : undefined}
          masks={{ in: '..... .....' }}
          placeholder={placeholder}
          inputProps={{
            id: id,
            name: name,
            required: required,
            // The library builds its own input, so the visible <label
            // htmlFor> above is the association - this is the belt to its
            // braces for the country-picker markup that sits between them.
            'aria-label': typeof label === 'string' ? label : undefined,
            // When the combined field swaps in the widget mid-typing it must
            // take focus - its focus event carries the dial code that
            // completes the handoff.
            autoFocus: allowEmail ? true : autoFocus,
            // `inputProps` is merged last, so these replace the library's own
            // handlers. Only take them over when this field can actually
            // become an email one - otherwise a stray letter would clear the
            // number the user has typed.
            ...(allowEmail ? swapHandlers : {}),
          }}
          disabled={disabled}
        />
      )}
      {helpText && <div className="text-sm text-grey font-400">{helpText}</div>}
      {errorText && (
        <div className="text-sm text-red-200 font-400">{errorText}</div>
      )}
      {errorList &&
        errorList.map((val, idx) => (
          <div role="alert" className="text-sm text-red-200 font-400" key={idx}>
            {val}
          </div>
        ))}
    </div>
  )
}

CustomPhoneInput.defaultProps = defaultProps
export default React.memo(CustomPhoneInput)
